/**
 * SANNTID I NETTLESEREN — én strøm, og alt den gjør er å invalidere (KOI Del 4).
 *
 * =================================================================================================
 * DEN SKRIVER ALDRI INN I CACHEN. Det er hele designet, og det er en sikkerhetsregel før det er en
 * arkitekturregel.
 *
 * Strømmen bærer id-er, ikke innhold (se migrering 0239 og `RealtimeService`): `NOTIFY` går utenom
 * RLS, så en nyttelast med meldingsteksten i ville vært den ene kanalen ingen policy rekker.
 * Klienten får «noe har skjedd, her er id-en», merker de riktige nøklene som foreldet, og React
 * Query henter gjennom de vanlige, beskyttede rutene.
 *
 * Bivirkningen er at koden er kjedelig, og det er en fordel: det finnes ingen andre vei inn i
 * cachen, ingen delvis oppdatert liste, og ingen mulighet for at en strøm og en henting er uenige.
 *
 * =================================================================================================
 * `fetch` OG IKKE `EventSource`, OG DET ER AUTENTISERINGEN SOM BESTEMMER DET.
 *
 * `EventSource` kan ikke sette headere. Tvitvi autentiserer med `Authorization: Bearer`, så en
 * `EventSource` måtte hatt tokenet i URL-en — og et JWT i en URL havner i tjenerlogger, i
 * `Referer`-headere og i nettleserhistorikken. Det er en ekte lekkasje for en bekvemmelighet.
 *
 * Prisen er at gjenoppkoblingen må skrives her. Den er tolv linjer og står under `connect()`:
 * eksponentiell pause opp til et tak, nullstilt når en strøm faktisk har levert noe.
 *
 * WIRE-FORMATET ER FORTSATT SSE. Tjeneren bruker Nests `@Sse()`, og parseren under leser
 * `event:`/`data:`-par — så bytter noen tilbake til `EventSource` den dagen vi får cookie-auth,
 * er det bare denne fila som endres.
 *
 * =================================================================================================
 * ÉN STRØM PER FANE, MONTERT ÉN GANG, ØVERST.
 *
 * Kroken hører hjemme i rotlayouten og ingen andre steder. To komponenter som hver åpnet sin ville
 * gitt to forbindelser per fane og to invalideringer per hendelse.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ORGANIZATION_HEADER } from '@tvitvi/shared';

import { apiUrl } from '@/api/client';
import { qk } from '@/api/query-keys';
import { getAccessToken } from '@/auth/supabase';
import { unreadNotificationsQueryOptions } from '@/features/overview/queries';

/** Det tjeneren sender. Bare id-er — se filhodet. */
interface RealtimeEvent {
  kind?: unknown;
  id?: unknown;
  threadId?: unknown;
  notificationType?: unknown;
}

export interface RealtimeHandlers {
  /**
   * Kalles når en NEVNING kommer inn, slik at siden kan si fra med en toast.
   *
   * Bare nevninger, og det er ordrens egen avgrensning: «toast for @-nevning når du er på en annen
   * side». Et varsel om at en oppgave forfaller er noe man ser i klokka når man ser etter; en
   * nevning er noen som snakker til deg.
   */
  onMention?: (notificationId: string) => void;
}

/*
 * HVA SOM ER EN NEVNING (QA6 K6, M7).
 *
 * `task.mentioned` er alltid en. `message.posted` er det bare når varselets nøkkel er nevningens:
 * en vanlig melding og en @-nevning i en tråd har den SAMME typen, og bare `i18nKey` skiller dem.
 * Med typen alene fikk alle «Noen nevnte deg» hver gang noen skrev i en tråd de var med i.
 *
 * Nøkkelen står ikke i strømmen, og skal ikke: `NOTIFY` går utenom RLS (se filhodet). Den hentes
 * derfor fra den vanlige, beskyttede varsellista — samme nøkkel som klokka, så menyen er varm når
 * man trykker. Finnes ikke raden der, blir det ingen toast: heller en toast for lite enn en falsk.
 */
const TASK_MENTION_TYPE = 'task.mentioned';
const MESSAGE_POSTED_TYPE = 'message.posted';
const MESSAGE_MENTION_KEY = 'notifications.messageMention.body';
/** Klokkas egne åtte (`BELL_ROWS`), så oppslaget og menyen deler cache-oppføring. */
const MENTION_LOOKUP_ROWS = 8;

/** Første pause etter et brudd, og taket den dobler seg opp til. */
const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 30_000;

/**
 * Åpner strømmen og invaliderer det som er blitt gammelt.
 *
 * `organizationId` er null før man har valgt hus. Da finnes det ingen strøm å åpne — og ingen
 * cache-nøkler å invalidere heller, siden hver eneste nøkkel begynner med organisasjonen.
 */
export function useRealtime(organizationId: string | null, handlers: RealtimeHandlers = {}): void {
  const client = useQueryClient();
  const onMention = handlers.onMention;

  useEffect(() => {
    if (organizationId === null) return;

    const controller = new AbortController();
    let retry: ReturnType<typeof setTimeout> | null = null;
    let wait = RETRY_MIN_MS;
    let stopped = false;

    const handle = (name: string, raw: string): void => {
      let parsed: RealtimeEvent;
      try {
        parsed = JSON.parse(raw) as RealtimeEvent;
      } catch {
        return;
      }

      if (name === 'notification') {
        /*
         * Klokka, lista og innboksens tall. Alle tre er avledet av den samme raden, og en av dem
         * som ikke oppdateres er et tall som er feil til neste navigasjon.
         */
        void client.invalidateQueries({ queryKey: qk.notifications(organizationId) });
        void client.invalidateQueries({ queryKey: qk.inboxUnreadCounts(organizationId) });

        const notificationId = parsed.id;
        if (onMention === undefined || typeof notificationId !== 'string') return;
        if (parsed.notificationType === TASK_MENTION_TYPE) {
          onMention(notificationId);
          return;
        }
        if (parsed.notificationType !== MESSAGE_POSTED_TYPE) return;
        void client
          .fetchQuery(unreadNotificationsQueryOptions(organizationId, MENTION_LOOKUP_ROWS))
          .then(
            (page) => {
              const row = page.items.find((item) => item.id === notificationId);
              if (!stopped && row?.i18nKey === MESSAGE_MENTION_KEY) onMention(notificationId);
            },
            () => undefined,
          );
        return;
      }

      if (name !== 'message' || typeof parsed.threadId !== 'string') return;

      /*
       * Tråden den kom i, OG trådlista — den siste fordi forhåndsvisningen og ulest-markøren står
       * der, og en innboks som ikke rører seg når noen skriver er nøyaktig det sanntid er til for.
       */
      void client.invalidateQueries({ queryKey: qk.thread(organizationId, parsed.threadId) });
      void client.invalidateQueries({ queryKey: qk.threads(organizationId) });
      void client.invalidateQueries({ queryKey: qk.inboxUnreadCounts(organizationId) });
    };

    const connect = async (): Promise<void> => {
      if (stopped) return;
      try {
        const token = await getAccessToken();
        const response = await fetch(apiUrl('/events/stream', undefined), {
          headers: {
            Accept: 'text/event-stream',
            ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
            [ORGANIZATION_HEADER]: organizationId,
          },
          signal: controller.signal,
        });
        if (!response.ok || response.body === null) throw new Error(String(response.status));

        wait = RETRY_MIN_MS;
        await readStream(response.body, handle, controller.signal);
      } catch {
        /*
         * ET BRUDD ER NORMALT, IKKE EN FEIL. En proxy som lukker, en laptop som sovner, en
         * API-omstart — alle tre ser like ut herfra, og svaret er det samme. Ingen logging: en
         * sovende maskin ville fylt konsollen med noe som ser ut som en feil og ikke er det.
         */
      }
      if (stopped) return;
      retry = setTimeout(() => void connect(), wait);
      wait = Math.min(wait * 2, RETRY_MAX_MS);
    };

    void connect();

    return () => {
      stopped = true;
      if (retry !== null) clearTimeout(retry);
      controller.abort();
    };
  }, [organizationId, client, onMention]);
}

/**
 * SSE-parseren: `event:`/`data:`-par skilt av en tom linje.
 *
 * Den holder en buffer fordi en TCP-pakke kan dele en hendelse på midten — det er den ene feilen
 * en håndskrevet SSE-leser gjør, og den viser seg først når en melding er lang nok.
 */
async function readStream(
  body: ReadableStream<Uint8Array>,
  handle: (name: string, data: string) => void,
  signal: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) return;

    buffer += decoder.decode(value, { stream: true });

    let split = buffer.indexOf('\n\n');
    while (split !== -1) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);

      let name = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) name = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (data.length > 0) handle(name, data);

      split = buffer.indexOf('\n\n');
    }
  }
}
